/**
 * usePopover — shared open/close + outside-click + Escape + focus
 * restoration for the editor's pill popovers (assignee, due date,
 * meeting date, format, project, ambiguity flag, etc.).
 *
 * Each popover used to inline-define this same useEffect: register
 * a `mousedown` listener while open, close when the click lands
 * outside `wrapperRef`. That worked but had three latent bugs:
 *
 *   1. **Stale closures** — the listener captured `value` and
 *      `draft` at the time it was registered. If the underlying
 *      value changed while the popover was open (autosave round
 *      trip, AI rewrite landing), an outside-click would persist
 *      the stale draft. Fixed here by reading state from refs that
 *      always hold the latest value.
 *   2. **No Escape handler** — only the input inside the popover
 *      bound Escape; clicking outside the input but inside the
 *      popover (e.g. a calendar cell) couldn't dismiss with Escape.
 *   3. **No focus restoration** — closing via outside click left
 *      focus on `document.body`, so keyboard users had to Tab back
 *      to the pill button. Fixed by stashing `document.activeElement`
 *      at open time and re-focusing it at close time (when the
 *      stash element is still in the DOM).
 *
 * Usage:
 *
 *   const popover = usePopover<HTMLSpanElement>({
 *     onCommit: () => { if (draft !== value) onChange(draft); },
 *   });
 *
 *   <span ref={popover.wrapperRef}>
 *     <button onClick={popover.toggle}>...</button>
 *     {popover.isOpen && <div>...popover content...</div>}
 *   </span>
 *
 * The hook returns a stable callback set; pill components don't
 * need to manage their own open state.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface PopoverApi<T extends HTMLElement> {
  /** Attach to the popover's outer wrapper element. */
  wrapperRef: React.RefObject<T>;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export interface UsePopoverOptions {
  /** Fires once when the popover closes — use to commit pending
   *  draft state to the underlying source. The hook reads the
   *  callback from a ref so closures capturing `draft` see the
   *  latest value (no stale-closure persistence bugs). */
  onCommit?: () => void;
  /** If false, outside click is ignored. Defaults to true. */
  closeOnOutsideClick?: boolean;
  /** If false, Escape is ignored. Defaults to true. */
  closeOnEscape?: boolean;
}

export function usePopover<T extends HTMLElement>(
  opts: UsePopoverOptions = {},
): PopoverApi<T> {
  const { closeOnOutsideClick = true, closeOnEscape = true } = opts;
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<T>(null);

  // Mirror onCommit into a ref so the listeners always see the
  // latest closure. This is the bug that bit us in
  // ActionItemView's pill popovers — the original `useEffect`
  // re-registered only when `open` changed, so subsequent
  // re-renders left a stale `onChange` reference behind that
  // would persist out-of-date state on outside-click.
  const onCommitRef = useRef(opts.onCommit);
  onCommitRef.current = opts.onCommit;

  // Stash whatever element had focus when the popover opened. On
  // close we re-focus it so keyboard users return to the pill
  // button, the editor surface, or wherever they came from.
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const close = useCallback(() => {
    if (onCommitRef.current) onCommitRef.current();
    setIsOpen(false);
    const prev = previousFocusRef.current;
    previousFocusRef.current = null;
    // Defer to the next microtask so React has a chance to unmount
    // the popover before we move focus — otherwise focus can land
    // on the popover's input mid-unmount and bounce.
    if (prev && document.contains(prev)) {
      queueMicrotask(() => {
        if (document.contains(prev)) prev.focus();
      });
    }
  }, []);

  const open = useCallback(() => {
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    setIsOpen(true);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((v) => {
      if (v) {
        if (onCommitRef.current) onCommitRef.current();
        const prev = previousFocusRef.current;
        previousFocusRef.current = null;
        if (prev && document.contains(prev)) {
          queueMicrotask(() => {
            if (document.contains(prev)) prev.focus();
          });
        }
        return false;
      }
      previousFocusRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      return true;
    });
  }, []);

  // Outside-click + Escape listeners. Re-registered only on
  // open-state change; they read latest state from refs.
  useEffect(() => {
    if (!isOpen) return;

    const onMouseDown = (e: MouseEvent) => {
      if (!closeOnOutsideClick) return;
      const wrapper = wrapperRef.current;
      if (wrapper && !wrapper.contains(e.target as Node)) {
        close();
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (!closeOnEscape) return;
      if (e.key !== 'Escape') return;
      const wrapper = wrapperRef.current;
      if (wrapper && wrapper.contains(e.target as Node)) {
        // Stop bubbling so other floating elements (BubbleMenu,
        // outer dialogs) don't ALSO close on the same Escape.
        e.stopPropagation();
        close();
      }
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [isOpen, close, closeOnOutsideClick, closeOnEscape]);

  return { wrapperRef, isOpen, open, close, toggle };
}
